import React from 'react';
import { Link } from 'react-router-dom';
import {
  ChevronLeft,
  ArrowRight,
  Clock,
  Sparkles,
  FolderTree,
  Briefcase,
  Layers,
} from 'lucide-react';
import SEOHead from '../components/SEOHead';
import Breadcrumbs from '../components/Breadcrumbs';

/**
 * Tier-3 consulting vertical piece targeting "organize files for a consulting
 * firm" and the client > engagement > phase structure that consulting work
 * actually follows. Consultants live in a Client > Engagement > phase world
 * (Proposal, Discovery, Working, Deliverables, Final), and the hard part is
 * that intake arrives as a mess of decks, transcripts, spreadsheets, and
 * signed SOWs that all look alike by filename. Sortio reads the content and
 * routes each file to the right engagement and phase. Positions Sortio as the
 * layer that names and files raw local files before they go into any practice
 * or project-management system, not as a replacement for one.
 */

const PUBLISHED = '2026-06-05';
const UPDATED = '2026-06-05';
const CANONICAL = 'https://www.getsortio.com/blog/organize-consulting-firm-files';

const FAQ = [
  {
    question: 'How should a consulting firm structure its client files?',
    answer:
      'Use three levels: Client, then Engagement, then phase. The top level is the client or account. Inside each client, one folder per engagement (a specific SOW or project, named with its year and a short title). Inside each engagement, five phase folders: Proposal, Discovery, Working, Deliverables, and Final. This mirrors how an engagement actually moves, so anyone on the team can find a document by remembering the client, the project, and roughly where in the lifecycle it was created. Sortio can build and maintain this structure automatically by reading each file and routing it to the right client, engagement, and phase.',
  },
  {
    question: 'What folders should each consulting engagement have?',
    answer:
      'Five phase folders cover almost every engagement: Proposal (the pitch, the SOW, the pricing, early scoping), Discovery (interviews, transcripts, data requests, current-state analysis), Working (in-progress models, working decks, internal drafts, meeting notes), Deliverables (the polished output the client receives: final decks, reports, recommendations), and Final (the signed acceptance, the wrap-up, the invoice, anything that closes the engagement). Keeping working drafts separate from delivered output is the single most useful split, because it stops anyone from sending a half-finished deck to the client by accident.',
  },
  {
    question: 'Should I organize by client first or by project type first?',
    answer:
      'Client first, almost always. Consulting work is relationship-driven and most retrieval questions start with "what did we do for this client." Project type (strategy, ops, diligence) varies inside a single client and is better captured in the engagement folder name than as a top-level split. The exception is a firm organized into rigid practice areas with no client overlap, but even then, Client > Engagement > phase inside each practice tends to win. Sortio lets you encode whichever order you choose in one prompt and applies it consistently to every file.',
  },
  {
    question: 'What is a good file naming convention for consulting deliverables?',
    answer:
      'A pattern that survives is {YYYY-MM-DD}_{Client}_{Engagement}_{DocType}_{vN}.ext, for example 2026-04-12_Acme_GrowthStrategy_Deliverable_v3.pptx. ISO date first so files sort chronologically, client and engagement so the name is meaningful outside its folder, a document type (Proposal, Notes, Model, Deliverable, SOW), and a version suffix because consulting documents go through many drafts. Version control in the filename is what keeps the Working and Deliverables folders from collapsing into a pile of "deck_final_FINAL_v2_real.pptx".',
  },
  {
    question: 'How do I keep consultants from saving files in the wrong place?',
    answer:
      'You stop relying on people to file correctly and let a watch folder do it. Point Sortio at a shared intake folder where the team drops anything, and it reads each file and routes it to the right client, engagement, and phase. On the Pro and Teams tiers a watch folder keeps doing this on every new arrival, so a consultant can drag a transcript or a deck into one place and it lands where it belongs without anyone thinking about the structure. Teams can also share a single set of rules so everyone files the same way.',
  },
  {
    question: 'Does Sortio replace a document management or project tool?',
    answer:
      'No. Sortio is the layer that gets your raw local files correctly named and filed before they go into whatever system you run. Most firms still have a stage where decks, transcripts, spreadsheets, and signed PDFs sit on a laptop or a shared drive with useless names. Sortio reads that content, names each file, and files it under the right client and engagement, so what flows into your downstream practice or project tool is already clean. It works alongside those tools rather than replacing them.',
  },
  {
    question: 'Can Sortio sort sensitive client material without sending it to the cloud?',
    answer:
      'Yes. Sortio supports local-only inference through Ollama, so the model runs on your own machine and no file content leaves it. That matters for consulting work under NDA, board material, financials, and competitively sensitive strategy documents. The managed AI option is faster and more accurate, and BYOK lets you point Sortio at your own provider key. Every sort is preview-before-apply and non-destructive, with a 30-day backup of any file Sortio renames or moves.',
  },
];

const OrganizeConsultingFirmFilesBlogPage = () => {
  const breadcrumbItems = [
    { label: 'Blog', href: null },
    { label: 'Organize consulting firm files by client and engagement', href: null },
  ];

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://www.getsortio.com/' },
      { '@type': 'ListItem', position: 2, name: 'Blog', item: CANONICAL },
      {
        '@type': 'ListItem',
        position: 3,
        name: 'Organize consulting firm files by client and engagement',
        item: CANONICAL,
      },
    ],
  };

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: 'How to Organize Files for a Consulting Firm by Client and Engagement',
    description:
      'Organize consulting firm files by Client, then Engagement, then phase (Proposal, Discovery, Working, Deliverables, Final). Folder tree, naming pattern, and a working Sortio prompt that files each document by reading its content.',
    url: CANONICAL,
    datePublished: PUBLISHED,
    dateModified: UPDATED,
    author: {
      '@type': 'Organization',
      name: 'Sortio',
      url: 'https://www.getsortio.com',
    },
    about: {
      '@type': 'SoftwareApplication',
      name: 'Sortio',
      url: 'https://www.getsortio.com',
    },
    publisher: {
      '@type': 'Organization',
      name: 'CMG Labs LLC',
      url: 'https://www.getsortio.com',
    },
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: FAQ.map((f) => ({
      '@type': 'Question',
      name: f.question,
      acceptedAnswer: { '@type': 'Answer', text: f.answer },
    })),
  };

  return (
    <div className="min-h-screen bg-black text-apple-100">
      <SEOHead
        title="How to Organize Files for a Consulting Firm by Client and Engagement"
        description="Organize consulting firm files by Client, then Engagement, then phase: Proposal, Discovery, Working, Deliverables, Final. Folder tree, naming pattern, and a working Sortio prompt included."
        canonical={CANONICAL}
        schema={[breadcrumbSchema, articleSchema, faqSchema]}
        keywords="organize consulting firm files, consulting firm file structure, organize files by client and engagement, consulting deliverables folder structure, consulting file naming convention, organize client engagement files, consulting document management"
        lastModified={UPDATED}
      />

      <div className="container mx-auto px-6 max-w-3xl py-16 lg:py-24">
        <Breadcrumbs items={breadcrumbItems} />

        <Link
          to="/articles"
          className="inline-flex items-center text-apple-300 hover:text-sortio-primary mb-8 transition-colors group"
        >
          <ChevronLeft className="w-4 h-4 mr-1 group-hover:-translate-x-1 transition-transform" />
          <span>All posts</span>
        </Link>

        <header className="mb-10">
          <span className="inline-block px-3 py-1 bg-sortio-primary/10 text-sortio-primary text-xs font-medium rounded-lg uppercase tracking-wider mb-4">
            Blog
          </span>
          <h1 className="text-4xl lg:text-5xl font-semibold text-apple-50 mb-6 leading-tight">
            How to Organize Files for a Consulting Firm by Client and Engagement
          </h1>
          <div className="flex items-center text-apple-400 text-sm gap-4">
            <span>By the Sortio team</span>
            <span className="flex items-center">
              <Clock className="mr-2" size={14} />
              Published {new Date(PUBLISHED).toLocaleDateString()}
            </span>
            <span>Updated {new Date(UPDATED).toLocaleDateString()}</span>
            <span>10 min read</span>
          </div>
        </header>

        <article className="prose-content space-y-6 text-apple-200 leading-relaxed">
          <p className="text-lg">
            Every consulting firm hits the same wall. A few clients in, the shared drive is a
            sprawl of decks, transcripts, spreadsheets, and signed SOWs, half of them named
            things like Untitled deck.pptx, notes.docx, and final_v2_REAL.xlsx. Nobody can find
            last quarter\'s deliverable for a client without asking the person who made it, and
            onboarding a new consultant means a tour of where things "tend to" live.
          </p>

          <h2 className="text-2xl font-semibold text-apple-50 mt-12 mb-4">
            How to organize files for a consulting firm by client and engagement
          </h2>

          <p>
            To organize files for a consulting firm by client and engagement, use three levels:
            Client, then Engagement, then phase (Proposal, Discovery, Working, Deliverables,
            Final). Sortio reads the content of each file and routes it to the right client,
            engagement, and phase automatically, so the structure holds even when intake arrives
            as a pile of generically named documents. That single hierarchy answers almost every
            retrieval question a firm has: which client, which project, and roughly where in the
            engagement lifecycle the document was created.
          </p>

          <p>
            The rest of this post is the full build: the exact folder tree, a naming convention
            that survives years of drafts, the difference between filing by hand, by rule, and by
            AI-on-content, and a working Sortio prompt you can paste in and run today.
          </p>

          <div className="bg-sortio-primary/10 border border-sortio-primary/20 rounded-2xl p-6 my-8 not-prose">
            <div className="flex items-start gap-3">
              <FolderTree className="text-sortio-primary flex-shrink-0 mt-0.5" size={20} />
              <div>
                <h3 className="font-medium text-apple-50 mb-2">The short version</h3>
                <p className="text-apple-300 text-sm leading-relaxed">
                  Structure: Client {'>'} Engagement {'>'} phase, where the five phases are
                  Proposal, Discovery, Working, Deliverables, Final. Naming:{' '}
                  {'{YYYY-MM-DD}_{Client}_{Engagement}_{DocType}_{vN}.ext'}. The hard part is
                  that intake all looks alike by filename, so you cannot file it with a rename
                  rule. Sortio reads each file\'s content, figures out the client, engagement, and
                  phase, names it to the template, and routes it. One prompt, applied
                  consistently, run as a watch folder so it keeps doing it.
                </p>
              </div>
            </div>
          </div>

          <h2 className="text-2xl font-semibold text-apple-50 mt-12 mb-4">
            The folder tree: Client, Engagement, phase
          </h2>

          <p>
            Here is the structure laid out. The top level is the client. Inside each client is one
            folder per engagement, named with the year and a short project title so engagements
            sort chronologically and read clearly. Inside each engagement are the five phase
            folders.
          </p>

          <pre className="bg-apple-700/30 rounded-lg p-4 overflow-x-auto text-sm text-apple-100 not-prose">
{`Clients/
  Acme-Corp/
    2026-Growth-Strategy/
      1-Proposal/
      2-Discovery/
      3-Working/
      4-Deliverables/
      5-Final/
    2025-Ops-Diagnostic/
      1-Proposal/
      2-Discovery/
      3-Working/
      4-Deliverables/
      5-Final/
  Northwind-Trading/
    2026-Market-Entry/
      1-Proposal/
      2-Discovery/
      3-Working/
      4-Deliverables/
      5-Final/`}
          </pre>

          <p>
            The number prefixes (1-Proposal, 2-Discovery) keep the phases in lifecycle order
            instead of alphabetical, so a folder always reads the way an engagement actually
            runs. Each phase has a clear job:
          </p>

          <ul className="list-disc pl-5 space-y-2">
            <li>
              <strong>Proposal</strong> holds the pitch, the SOW, pricing, and early scoping. This
              is everything from before the engagement was won.
            </li>
            <li>
              <strong>Discovery</strong> holds interviews, call transcripts, data requests, and
              current-state analysis. The raw input you gather before you build anything.
            </li>
            <li>
              <strong>Working</strong> holds in-progress models, working decks, internal drafts,
              and meeting notes. The messy middle, deliberately kept separate from finished output.
            </li>
            <li>
              <strong>Deliverables</strong> holds the polished output the client actually receives:
              final decks, reports, and recommendations.
            </li>
            <li>
              <strong>Final</strong> holds the signed acceptance, the wrap-up, the invoice, and
              anything that closes the engagement out.
            </li>
          </ul>

          <p>
            The most valuable split here is Working versus Deliverables. Keeping in-progress drafts
            physically apart from delivered output is what stops a half-finished deck from going to
            the client by accident. For the more general project-folder version of this pattern, see{' '}
            <Link to="/blog/folder-structure-client-projects" className="text-sortio-primary hover:underline">
              the best folder structure for client projects
            </Link>
            .
          </p>

          <h2 className="text-2xl font-semibold text-apple-50 mt-12 mb-4">
            A naming convention that survives years of drafts
          </h2>

          <p>
            The structure is only half the system. The filenames inside it have to be meaningful
            on their own, because they get emailed, downloaded, and pulled out of context all the
            time. The pattern that holds up is:
          </p>

          <pre className="bg-apple-700/30 rounded-lg p-4 overflow-x-auto text-sm text-apple-100 not-prose">
{`{YYYY-MM-DD}_{Client}_{Engagement}_{DocType}_{vN}.ext

2026-04-12_Acme_GrowthStrategy_Deliverable_v3.pptx
2026-03-28_Acme_GrowthStrategy_DiscoveryNotes_v1.docx
2026-02-15_Acme_GrowthStrategy_SOW_v2.pdf
2026-04-09_Northwind_MarketEntry_Model_v5.xlsx`}
          </pre>

          <p>
            Five parts. ISO date first (YYYY-MM-DD) so files sort chronologically with no custom
            view. Client and engagement next so the filename is meaningful even when it leaves the
            folder. A document type (SOW, Proposal, DiscoveryNotes, Model, Deliverable). And a
            version suffix, because consulting documents go through many drafts and version control
            in the filename is the only thing that keeps a folder from collapsing into
            deck_final_FINAL_v2_real.pptx. If you want to generate and document a convention like
            this for your whole firm, the{' '}
            <Link to="/tools/file-naming-convention-generator" className="text-sortio-primary hover:underline">
              file naming convention generator
            </Link>{' '}
            builds one you can hand to the team.
          </p>

          <p>
            Consistency is the entire point of a convention, not the specific tokens you pick.
            Stanford University Libraries makes the same point in its{' '}
            <a
              href="https://guides.library.stanford.edu/data-best-practices"
              className="text-sortio-primary hover:underline"
              target="_blank"
              rel="noopener noreferrer"
            >
              data management best practices
            </a>
            : a naming scheme only pays off if every file follows it, which is exactly where
            human discipline tends to break down and where an automated filing layer earns its
            keep.
          </p>

          <h2 className="text-2xl font-semibold text-apple-50 mt-12 mb-4">
            Manual vs rule-based vs AI-by-content
          </h2>

          <p>
            There are three ways to keep this structure populated. They are not equivalent, and
            the reason consulting filing fails is almost always that the firm is using the wrong
            one for the job.
          </p>

          <div className="overflow-x-auto not-prose my-6">
            <table className="w-full text-sm text-left border-collapse">
              <thead>
                <tr className="border-b border-apple-700/50">
                  <th className="py-3 pr-4 text-apple-50 font-semibold">Approach</th>
                  <th className="py-3 pr-4 text-apple-50 font-semibold">How it decides where a file goes</th>
                  <th className="py-3 text-apple-50 font-semibold">Where it breaks</th>
                </tr>
              </thead>
              <tbody className="text-apple-300">
                <tr className="border-b border-apple-700/30">
                  <td className="py-3 pr-4 align-top font-medium text-apple-100">Manual</td>
                  <td className="py-3 pr-4 align-top">
                    A person reads the file and drags it to a folder, renaming it by hand.
                  </td>
                  <td className="py-3 align-top">
                    Accurate but never happens. People save to the desktop "for now" and the
                    structure rots. Onboarding requires tribal knowledge.
                  </td>
                </tr>
                <tr className="border-b border-apple-700/30">
                  <td className="py-3 pr-4 align-top font-medium text-apple-100">Rule-based</td>
                  <td className="py-3 pr-4 align-top">
                    A rule matches on filename, extension, or folder (if the name contains "SOW",
                    move to Proposal).
                  </td>
                  <td className="py-3 align-top">
                    Works only when filenames are already meaningful. Consulting intake is named
                    Untitled deck.pptx and notes.docx, so there is nothing for a rule to match on.
                  </td>
                </tr>
                <tr>
                  <td className="py-3 pr-4 align-top font-medium text-apple-100">AI-by-content</td>
                  <td className="py-3 pr-4 align-top">
                    The tool reads what is inside the file (the deck, the transcript, the signed
                    PDF) and decides the client, engagement, and phase from the meaning.
                  </td>
                  <td className="py-3 align-top">
                    Uses your AI allowance and is slower than a rule. Overkill for a single stable
                    vendor format, ideal for mixed, generically named intake.
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <p>
            Consulting filing lives squarely in the AI-by-content column, because the input is a
            heterogeneous pile of documents whose names tell you nothing. Once a pattern is stable
            and the filenames become predictable, you can promote individual routes to a
            deterministic rule that does not use your AI allowance at all. The decision of which to use
            when is covered in{' '}
            <Link to="/blog/ai-sort-vs-rule-builder-which-to-use" className="text-sortio-primary hover:underline">
              AI Sort vs Rule Builder
            </Link>
            .
          </p>

          <h2 className="text-2xl font-semibold text-apple-50 mt-12 mb-4">
            How Sortio files a consulting document
          </h2>

          <p>
            Sortio is a desktop AI file organizer for macOS and Windows that reads file content
            with an LLM and sorts, renames, and routes by meaning. For a consulting firm the flow
            is the same whether you run it on one file or a quarter\'s backlog. Sortio opens each
            document, reads the content (the deck, the transcript, the SOW, the model), and
            decides three things: which client it belongs to, which engagement, and which phase.
            It then names the file to your template and moves it into Client {'>'} Engagement {'>'}{' '}
            phase.
          </p>

          <p>
            The reason this works where a rename rule does not is that the LLM reads meaning, not
            the filename. A transcript titled Zoom_recording_0412.docx is recognized as a discovery
            interview for Acme\'s growth engagement because of what is in it. A deck named
            Untitled.pptx is recognized as a working draft versus a finished deliverable based on
            its content and completeness. Every sort is preview-before-apply and non-destructive,
            and Sortio keeps a 30-day backup of any file it renames or moves, so you can review
            every proposed move before anything happens and revert later if needed.
          </p>

          <h2 className="text-2xl font-semibold text-apple-50 mt-12 mb-4">
            A working Sortio prompt for consulting files
          </h2>

          <p>
            Point Sortio at a shared intake folder (or a single client\'s messy folder) and paste
            this in. Run Preview before applying.
          </p>

          <pre className="bg-apple-700/30 rounded-lg p-4 overflow-x-auto text-sm text-apple-100 not-prose">
{`Read each file and file it under
~/Clients/{Client}/{Year}-{Engagement}/ into one of five
phase folders, choosing the phase from the content:

1-Proposal   - pitch decks, SOWs, pricing, scoping docs
               sent before the work was won
2-Discovery  - interview notes, call transcripts, data
               requests, current-state analysis
3-Working    - in-progress models, working decks,
               internal drafts, meeting notes
4-Deliverables - polished decks, reports, and
                 recommendations meant for the client
5-Final      - signed acceptance, wrap-up docs, invoices

Identify the Client and the Engagement from the document
content (company name, project name, SOW title). Use a
short canonical client name (Acme not Acme Corporation
Inc).

Rename each file to:
{YYYY-MM-DD}_{Client}_{Engagement}_{DocType}_{vN}.ext

DocType is one of: SOW, Proposal, DiscoveryNotes,
Transcript, Model, Deliverable, Invoice, Other. Use the
document date for YYYY-MM-DD. Detect the version (vN)
from the content or default to v1.

If you cannot confidently determine the client or
engagement, leave the file in ~/Clients/_Unsorted/
untouched.`}
          </pre>

          <p>
            Click Preview. Sortio shows the proposed name, destination, and the client, engagement,
            and phase it inferred for every file. If Sortio mislabels a file, untick it so it stays
            put, or tighten the prompt and preview again. Nothing moves until you apply.
            The _Unsorted fallback is deliberate: it is better to leave one ambiguous file unfiled
            than to guess wrong on a client deliverable.
          </p>

          <h2 className="text-2xl font-semibold text-apple-50 mt-12 mb-4">
            Keeping it clean with a watch folder
          </h2>

          <p>
            Cleaning the backlog once is satisfying. Keeping it clean is what actually changes how
            the firm works. On Sortio Pro ($14.99/month or $99/year) you can turn the prompt above
            into a watch folder on your intake directory. The workflow:
          </p>

          <ol className="list-decimal pl-5 space-y-2">
            <li>
              Pick the folder where the team drops work. A single shared "Inbox" everyone has
              access to works best, so consultants never have to think about where a file goes.
            </li>
            <li>
              In Sortio, open Watch Folders, add that folder, and paste the consulting prompt.
            </li>
            <li>
              For the first week, leave the watch in Preview mode. Sortio queues proposed moves and
              notifies you instead of applying them, so you can confirm the AI is reading
              engagements correctly before you trust it.
            </li>
            <li>
              After a week of clean previews, switch to Apply. From then on, anything dropped into
              the Inbox is read, named, and filed under the right client, engagement, and phase
              automatically.
            </li>
          </ol>

          <p>
            For a multi-person firm, the Teams tier ($29/seat/month) adds shared rules and an admin
            console, so every consultant files the same way from the same rule set instead of each
            person inventing their own structure. This is also how you stop people from saving in
            the wrong place: you remove the decision entirely. For more on cutting the time the team
            spends filing and renaming, see{' '}
            <Link to="/blog/reduce-time-filing-renaming-documents" className="text-sortio-primary hover:underline">
              how to reduce the time your team spends filing and renaming documents
            </Link>
            .
          </p>

          <h2 className="text-2xl font-semibold text-apple-50 mt-12 mb-4">
            Where Sortio fits with your other tools
          </h2>

          <p>
            Most firms run some downstream system: a project tool, a shared drive with a strict
            structure, or a document-management platform. Sortio is not a replacement for any of
            those. It is the layer that gets your raw local files correctly named and filed before
            they go into one. There is almost always a stage where decks, transcripts, and signed
            PDFs sit on a laptop or in a shared folder with useless names, and that is the gap
            Sortio closes. What flows into your downstream tool is already clean, consistently
            named, and filed under the right client and engagement.
          </p>

          <p>
            For sensitive material, NDA work, board decks, financials, competitively sensitive
            strategy, Sortio supports local-only inference through Ollama, so the model runs on your
            own machine and no file content leaves it. The managed AI option is faster and more
            accurate, and BYOK lets you use your own provider key. The deeper coverage of this
            tradeoff is in{' '}
            <Link to="/blog/local-ai-vs-cloud-ai-file-organization" className="text-sortio-primary hover:underline">
              local AI vs cloud AI for file organization
            </Link>
            . If you want the consulting-specific overview of how Sortio fits a practice, see the{' '}
            <Link to="/for-consultants" className="text-sortio-primary hover:underline">
              Sortio for consultants
            </Link>{' '}
            page.
          </p>

          <h2 className="text-2xl font-semibold text-apple-50 mt-12 mb-4">
            Enforcing the convention across the firm
          </h2>

          <p>
            A naming convention only works if every file follows it, and that is precisely where a
            human-discipline approach fails. The value of running this through Sortio (and, for
            stable patterns, the AI Rule Builder) is that the convention gets enforced by the tool
            rather than remembered by the person. Files arrive named correctly because the system
            named them, not because a consultant looked up the standard. For the full playbook on
            making a naming standard actually stick, see{' '}
            <Link to="/blog/enforce-file-naming-convention" className="text-sortio-primary hover:underline">
              how to enforce a file naming convention
            </Link>
            .
          </p>

          <h2 className="text-2xl font-semibold text-apple-50 mt-12 mb-4">
            FAQ
          </h2>

          <div className="space-y-4 not-prose">
            {FAQ.map((f, i) => (
              <div key={i} className="bg-apple-700/20 rounded-xl p-6">
                <h3 className="font-medium text-apple-50 mb-3">{f.question}</h3>
                <p className="text-apple-300 leading-relaxed">{f.answer}</p>
              </div>
            ))}
          </div>
        </article>

        {/* Internal links */}
        <section className="mt-16 pt-8 border-t border-apple-700/30">
          <h2 className="text-xl font-semibold text-apple-50 mb-4">Keep reading</h2>
          <div className="grid gap-3">
            <Link
              to="/blog/folder-structure-client-projects"
              className="apple-panel p-4 rounded-apple group hover:ring-1 hover:ring-sortio-primary/30 transition-all flex items-center justify-between"
            >
              <span className="text-apple-100 group-hover:text-sortio-primary transition-colors">
                The best folder structure for client projects
              </span>
              <ArrowRight className="w-4 h-4 text-apple-400 group-hover:text-sortio-primary group-hover:translate-x-1 transition-all" />
            </Link>
            <Link
              to="/blog/enforce-file-naming-convention"
              className="apple-panel p-4 rounded-apple group hover:ring-1 hover:ring-sortio-primary/30 transition-all flex items-center justify-between"
            >
              <span className="text-apple-100 group-hover:text-sortio-primary transition-colors">
                How to enforce a file naming convention
              </span>
              <ArrowRight className="w-4 h-4 text-apple-400 group-hover:text-sortio-primary group-hover:translate-x-1 transition-all" />
            </Link>
            <Link
              to="/blog/reduce-time-filing-renaming-documents"
              className="apple-panel p-4 rounded-apple group hover:ring-1 hover:ring-sortio-primary/30 transition-all flex items-center justify-between"
            >
              <span className="text-apple-100 group-hover:text-sortio-primary transition-colors">
                Reduce the time you spend filing and renaming documents
              </span>
              <ArrowRight className="w-4 h-4 text-apple-400 group-hover:text-sortio-primary group-hover:translate-x-1 transition-all" />
            </Link>
          </div>
        </section>

        <section className="mt-12">
          <div className="bg-gradient-to-r from-sortio-primary/10 to-sortio-secondary/10 rounded-2xl p-8 border border-sortio-primary/20">
            <div className="flex items-center mb-4">
              <Sparkles className="text-sortio-primary mr-3" size={24} />
              <h2 className="text-2xl font-semibold text-apple-50">Clean up one client folder first</h2>
            </div>
            <p className="text-apple-300 mb-6 leading-relaxed">
              The free tier includes a one-time AI trial allowance, no card required. Point Sortio at one
              client\'s messiest folder, paste the consulting prompt, and run Preview to see the
              Client {'>'} Engagement {'>'} phase structure it would build before anything moves.
            </p>
            <Link
              to="/download"
              className="inline-flex items-center px-6 py-3 bg-sortio-primary hover:bg-sortio-primary/90 text-white font-medium rounded-xl transition-colors duration-200"
            >
              Download Sortio
              <ArrowRight className="ml-2" size={16} />
            </Link>
          </div>
        </section>
      </div>
    </div>
  );
};

export default OrganizeConsultingFirmFilesBlogPage;
